<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useAbility } from "@casl/vue";

import PageHeader from "@/components/ui/headers/PageHeader.vue";
import { useActiveSubNavTitle } from "@/components/shared/navbar/useNavbarSubItems";
import BtnToRooms from "@/components/shared/BtnToRooms.vue";
import {
  type EPatientDashboardTab,
  usePatientDashboard,
  usePatientDashboardTabFilters,
} from "@/composables/models/patient/usePatientDashboard.ts";
import type {
  IPatientDashboardFilters,
  TPatientDashboardItem,
} from "@/api/models/patient/dashboard";
import { tableRowClick } from "@/utils/tableRowClick";
import FilterSelect from "@/components/ui/inputs/FilterSelect.vue";
import TableFilterTabs from "@/components/shared/table/TableFilterTabs.vue";
import PatientDashboardTable from "@/components/models/patient/table/PatientDashboardTable.vue";
import GeneralTableNoData from "@/components/shared/table/GeneralTableNoData.vue";
import PatientDashboardWelcome from "@/components/models/patient/table/PatientDashboardWelcome.vue";
import FilterDateRange from "@/components/ui/inputs/FilterDateRange.vue";
import {
  PATIENT_DASHBOARD_HEADERS,
  useActiveTreatmentTypeItems,
} from "@/composables/models/patient/usePatientDashboardTable.ts";
import type { TFilter } from "@/types/filter/tableFilters.types.ts";
import { addPatientAction } from "@/components/ui/headers/pageHeaderActions.ts";

const { t } = useI18n();
const { can } = useAbility();
const router = useRouter();

definePage({
  meta: {
    title: "generic.entity.dashboard",
    privilegeScope: "section",
    checkPrivileges: () => {
      const { can } = useAbility();
      return can($permActions.READ, $permSubjects.PATIENT__PATIENT);
    },
  },
});

const headerActions = computed(() => addPatientAction(t, can));

const pageTitle = useActiveSubNavTitle();

const activeTreatmentTypeItems = useActiveTreatmentTypeItems();

// `TableFilterTabs` owns the `?tab=` url sync and preselects the first filter,
// so the page only holds the resulting tab.
const tabFilters = usePatientDashboardTabFilters();
const activeTab = ref<TFilter<EPatientDashboardTab, IPatientDashboardFilters> | null>(null);

// Search/sort live on the query (`url` mode); the UI filters are declared as `fields` on the
// composable, so the query owns their state, their URL sync, and the request filters they map
// to — the page only binds `query.f.*` in the template. `PATIENT_DASHBOARD_HEADERS` is the same
// set `PatientDashboardTable` renders, passed here so URL `ordering` stores the backend field
// (`patient` → `last_name`) while the header arrow keeps the column key.
const query = usePatientDashboard({
  url: { useQuery: true, headers: PATIENT_DASHBOARD_HEADERS },
  initial: { page_size: 25 },
  tabs: activeTab,
  // `TableFilterTabs` resolves the active tab (from `?tab=` or its first
  // filter) right after mount. Waiting for it avoids firing an extra request
  // with no tab filter, which would immediately be superseded.
  enabled: computed(() => activeTab.value !== null),
});

// Unfiltered probe — an empty main table can't tell a new account from filters matching nothing.
const anyPatientQuery = usePatientDashboard({ initial: { page_size: 1 } });

const showWelcome = computed(
  () =>
    !anyPatientQuery.isLoading.value &&
    (anyPatientQuery.data.value?.pages[0]?.results?.length ?? 0) === 0
);

const onRowClick = async (event: MouseEvent, row: Record<"item", TPatientDashboardItem>) => {
  if (row.item.id) {
    await tableRowClick(event, router, {
      name: "/rooms/patient/[id]",
      params: { id: row.item.id },
    });
  }
};
</script>

<template>
  <div
    class="tw:relative"
    data-tour="dashboard-header"
  >
    <btn-to-rooms />

    <page-header
      :title="pageTitle"
      search-data-tour="dashboard-search"
      :search-placeholder="t('generic.tables.searchByNameOrEmail')"
      :actions="headerActions"
    >
      <template #filters>
        <filter-select
          v-model="query.f.activeTreatmentType"
          :items="activeTreatmentTypeItems"
          :label="t('models.patient.phasePlan.plan')"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[9.5rem] tw:sm:min-w-[9.5rem]"
        />

        <filter-date-range
          v-model="query.f.lastUpdate"
          field-name="last_update"
          :label="t('models.patient.table.filter.lastUpdate')"
          :max="new Date()"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[9.5rem] tw:sm:min-w-[9.5rem]"
        />
      </template>
    </page-header>

    <div class="tw:rounded-[10px]">
      <div data-tour="dashboard-overview">
        <table-filter-tabs
          v-model:active-tab="activeTab"
          :filters="tabFilters"
          :loading="query.isLoading.value"
          query-key="tab"
        />
      </div>
      <div
        data-tour="dashboard-main-container"
        class="tw:rounded-[10px]"
      >
        <div class="tw:relative">
          <patient-dashboard-table
            :query="query"
            :row-click="onRowClick"
            elevated-animation
            data-test-id="patient-table"
            :row-props="{
              'data-test-id': 'patient-table-item',
              'data-tour': 'dashboard-patient-row',
            }"
            :prefetch-viewports="0.9"
            mobile-expandable
          >
            <template #no-data>
              <patient-dashboard-welcome
                v-if="showWelcome"
                data-testid="patient-no-data"
              />
              <general-table-no-data
                v-else
                data-test-id="patient-no-data"
              />
            </template>
          </patient-dashboard-table>
        </div>
      </div>
    </div>
  </div>
</template>
